<script setup lang="ts">
import { PopoverArrow, PopoverClose, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from "reka-ui";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import StringTooltip from "@/components/Tooltip/StringTooltip.vue";

import {
  getMarkEvidences,
  isTimeIndexRedaction,
  isTranscriptAnnotation,
  toDuration,
} from "@/services/UtilitiesService";
import { deleteAnnotation, isRedaction } from "@/services/AnnotationService";

import { useAnnotationStore } from "@/stores/annotation-store";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import type { ParsedMark } from "@/models/Mark";
import type { TimeIndexRedaction, TranscriptAnnotation } from "@/models/Annotation";

withDefaults(defineProps<{
  annotation?: string;
  fileReference: string;
}>(), {  })

const $router = useRouter();
const routeParams = useCaseRouteParams();

const openDeletePopup = ref<Record<string, boolean>>({});

const annotations = computed(() =>
  useAnnotationStore()
    .annotations.filter(
      (mark): mark is ParsedMark<TranscriptAnnotation> | ParsedMark<TimeIndexRedaction> =>
        /user/i.test(mark.source) &&
        mark.fileReference === __props.fileReference &&
        (isTranscriptAnnotation(mark.parsedMarkData) || isTimeIndexRedaction(mark.parsedMarkData)),
    )
    .sort(
      (a, b) =>
        a.parsedMarkData.startTime - b.parsedMarkData.startTime ||
        a.parsedMarkData.endTime - b.parsedMarkData.endTime ||
        a.parsedMarkData.type.localeCompare(b.parsedMarkData.type),
    ),
);

function goToAnnotation(annotationId: string) {
  $router.push({
    name: "casefileannotation",
    params: {
      brand: routeParams.value.brand,
      casename: routeParams.value.casename,
      casefile: __props.fileReference,
      annotation: annotationId,
    },
    force: true,
  });
}

async function onDeleteAnnotation(mark: ParsedMark, annotationId: string) {
  await deleteAnnotation(mark);
  openDeletePopup.value[annotationId] = false;

  if (__props.annotation !== annotationId) return;

  $router.push({
    name: "casefile",
    params: {
      brand: routeParams.value.brand,
      casename: routeParams.value.casename,
      casefile: __props.fileReference,
    },
  });
}
</script>
<template>
  <div
    class="tw-flex tw-h-full tw-w-1/4 tw-flex-col tw-items-start tw-justify-start tw-gap-1 tw-divide-y tw-bg-[#e7e9ef] tw-shadow"
  >
    <div
      v-if="annotations.length < 1"
      class="tw-grid tw-w-full tw-grow tw-place-items-center tw-text-pretty tw-text-center"
    >
      No annotations created
    </div>
    <div v-else class="tw-w-full tw-grow tw-overflow-y-auto tw-p-1">
      <div
        v-for="mark in annotations"
        :key="mark.parsedMarkData.uuid"
        tabindex="0"
        class="tw-cursor-pointer tw-select-none tw-rounded-md tw-px-2 tw-py-1 hover:tw-bg-white hover:tw-brightness-100"
        :class="{ 'tw-brightness-90': mark.parsedMarkData.uuid === annotation }"
        @click="() => goToAnnotation(mark.parsedMarkData.uuid)"
      >
        <div
          v-for="{ bookmarkCount } in [{ bookmarkCount: getMarkEvidences({ uid: mark.parsedMarkData.uuid }).length }]"
          :key="bookmarkCount"
          class="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2"
        >
          <span>
            {{ toDuration(mark.parsedMarkData.startTime, true) }} -
            {{ toDuration(mark.parsedMarkData.endTime, true) }}
          </span>
          <div class="tw-flex tw-items-center tw-justify-start tw-gap-2">
            <StringTooltip v-if="isRedaction(mark.parsedMarkData)" item="Redaction">
              <span class="tw-aspect-square tw-h-3.5 tw-w-3.5 tw-bg-black" />
            </StringTooltip>
            <StringTooltip v-else :item="`${bookmarkCount} ${bookmarkCount === 1 ? 'bookmark' : 'bookmarks'}`">
              <span>
                <i class="fa-bookmark" :class="bookmarkCount ? 'fas' : 'far'" />
                {{ bookmarkCount }}
              </span>
            </StringTooltip>
            <PopoverRoot v-model:open="openDeletePopup[mark.parsedMarkData.uuid]">
              <PopoverTrigger @click.stop>
                <i class="fa fa-trash" />
              </PopoverTrigger>
              <PopoverPortal>
                <PopoverContent
                  avoid-collisions
                  hide-when-detached
                  class="tw-z-[1000] tw-flex tw-select-none tw-flex-col tw-items-end tw-justify-center tw-gap-2 tw-rounded-md tw-border tw-bg-white tw-p-2 tw-text-xs tw-shadow"
                >
                  <p class="tw-max-w-xs">
                    {{
                      bookmarkCount > 0
                        ? "This annotation has associated bookmarks. Are you sure you'd like to delete this annotation and all its associated bookmarks?"
                        : "Are you sure you want to delete this annotation?"
                    }}
                  </p>
                  <div class="tw-flex tw-items-center tw-justify-end tw-gap-2">
                    <PopoverClose class="tw-btn-md-gray">Cancel</PopoverClose>
                    <button
                      type="button"
                      class="tw-rounded-md tw-px-2 tw-py-1 hover:tw-bg-red-500/40"
                      @click="() => onDeleteAnnotation(mark, mark.parsedMarkData.uuid)"
                    >
                      Delete
                    </button>
                  </div>
                  <PopoverArrow class="tw-fill-white" />
                </PopoverContent>
              </PopoverPortal>
            </PopoverRoot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
